/**
 * `GameState` — the flat, plain-data record of a game in progress (SPEC.md §4.2, §4.4).
 *
 * Everything here is plain data: numbers, strings, booleans, arrays and object literals.
 * No classes, no `Map`/`Set`, no functions (the one exception is the shared, immutable
 * `catalog`). That is what makes `clone` a cheap hand-written copy, `apply` a mutation,
 * and the whole state trivially comparable and hashable.
 */
import type { AbilityInstance, BagState } from './bag.ts';
import type { CustomChoiceSpec, CustomRun } from './abilities/custom.ts';
import type {
  DistributeSpec,
  Effect,
  PlayerRef,
  PlayerTargetSpec,
  TargetSpec,
} from './abilities/ir.ts';
import type { CardCatalog } from './cards.ts';
import type { ChoiceRequest } from './choices.ts';
import type { CostModifier } from './costs.ts';
import type { GameEvent } from './events.ts';
import type { InstanceId, PlayerIndex, Zone } from './ids.ts';
import type { Modifier } from './modifiers.ts';
import type { RngState } from './rng.ts';
import type { CardStack } from './stacks.ts';

/** Where the turn is. `setup` covers dealing and altering hands. */
export type TurnPhase = 'setup' | 'start-of-turn' | 'main' | 'end-of-turn' | 'game-over';

/**
 * One physical card. `defId` is the LorcanaJSON id; everything else is per-game state.
 *
 * `knownTo` is a bitmask of the players who know this instance's identity — hand cards
 * (owner), your own inkwell (owner), anything in a public zone (everyone), and the leaks
 * CR §7 creates (revealed-then-shuffled, returned-to-hand). Knowledge is monotone: zone
 * changes only ever add knowers (`zones.ts`). It says nothing about *where* a card is
 * among a deck's positions, which is why `playerView` never names a deck card and
 * `determinize` re-deals the identities known to be in a deck. Both derive from this one
 * field (SPEC.md §4.4).
 */
export interface CardInstance {
  readonly id: InstanceId;
  /** Whose deck it started in. Cards return to their owner's discard.  */
  readonly owner: PlayerIndex;
  /** Who currently controls it; differs from `owner` only under future effects. */
  controller: PlayerIndex;
  defId: number;
  zone: Zone;
  /** Exerted cards are turned sideways: spent ink, questing or challenging characters. */
  exerted: boolean;
  /** Damage counters on the card. Cleared when it leaves play (CR §1.9). */
  damage: number;
  /** True until the start of its controller's next turn — a *drying* character (CR §5.1). */
  drying: boolean;
  /**
   * The location this character is at, or `null` (CR §4.3.6). Only a character in play is
   * ever at one, and it stops being there the moment either card leaves play — the same
   * lifetime as damage counters, and cleared in the same place (`zones.ts`).
   */
  location: InstanceId | null;
  /** Bitmask of players who know this card's identity. */
  knownTo: number;
}

/** One player's zones and counters. Zones are ordered: `deck[0]` is the top of the deck. */
export interface PlayerState {
  readonly index: PlayerIndex;
  deck: InstanceId[];
  hand: InstanceId[];
  play: InstanceId[];
  inkwell: InstanceId[];
  discard: InstanceId[];
  /**
   * Cards under one of this player's cards in play — Shift bases and Boost's facedown cards
   * (CR §8.10.4, §8.3). **Not in play.** This is the zone array that keeps them accounted for;
   * which pile each one belongs to, and in what order, is `GameState.stacks`.
   */
  under: InstanceId[];
  /**
   * Cards of this player's own deck they are currently **looking at** (CR §7.9) — the cards a
   * resolving "look at the top N cards of your deck" has in front of them. They are still in
   * the deck zone, so zone conservation is untouched; what this records is that their owner
   * (and, for a public reveal, everyone with the `knownTo` bit) may see them where they are.
   *
   * Always empty between abilities: the resolution of an ability clears it.
   */
  looking: InstanceId[];
  lore: number;
  /**
   * How many cards this player has inked this turn; reset every Ready step. The CR §4.2
   * limit it is checked against is **computed on read** (`inkLimitOf`): 1 plus any live
   * "ink an additional card" statics, so the count is the only stored truth.
   */
  inksThisTurn: number;
  /**
   * How many cards have been put into this player's inkwell this turn, **however** they got
   * there — the turn action above and every effect that puts a card there (CR §7.5). This is
   * what "if it's the second card you've put into your inkwell this turn" counts; the ink
   * *action* has its own count because it is also the once-per-turn limit.
   */
  cardsInkedThisTurn: number;
  /**
   * How many cards this player has drawn this turn, **however** they got there — the draw step
   * and every effect that draws a card alike (mirrors `cardsInkedThisTurn`). Incremented at
   * `drawCard`'s single chokepoint (`zones.ts`), only when a card is actually drawn — an empty
   * deck's failed draw does not count.
   */
  cardsDrawnThisTurn: number;
  /**
   * How many cards have been put into this player's discard this turn, by any cause — a
   * discard from hand, an Action finishing, a banish, or any other zone move. Incremented at
   * `moveInstance`'s single chokepoint only when a card actually enters the discard.
   */
  cardsPutIntoDiscardThisTurn: number;
  /**
   * How many cards this player has **discarded** this turn (CR §7.3) — narrower than
   * `cardsPutIntoDiscardThisTurn`: only a hand→discard move counts, so a banish never
   * increments it. Incremented at `zones.ts`'s `card-discarded` emission sites.
   */
  cardsDiscardedThisTurn: number;
  /** Whether this player altered their opening hand (they shuffle afterwards, CR §2.2.2). */
  alteredHand: boolean;
}

/**
 * One frame of the explicit resolution stack (SPEC.md §4.3). The top frame is what
 * generates the current `ChoiceRequest`; no game logic ever lives in suspended JS call
 * frames, which is what makes a mid-turn state cloneable and serializable.
 *
 * `game` is the permanent bottom frame: stepping it starts the next turn.
 */
export type Frame =
  | { readonly kind: 'game' }
  | { readonly kind: 'mulligan'; readonly player: PlayerIndex; selected: InstanceId[] }
  | {
      readonly kind: 'turn-start';
      step: 'ready' | 'ready-choice' | 'set' | 'draw';
      /**
       * Populated when the Ready step pauses on a live ready-cap choice ("each opponent
       * can't ready more than N of their characters", Marshmallow - Cranky Climber): the
       * characters still exerted and eligible, the cap, and which of them have been picked
       * so far. Cleared once the choice finishes and `step` moves on to `'set'`.
       */
      readyChoice: { readonly eligible: InstanceId[]; readonly cap: number; chosen: InstanceId[] } | null;
    }
  | { readonly kind: 'main' }
  | {
      readonly kind: 'challenge';
      readonly challenger: InstanceId;
      step: 'choose-target' | 'resolve';
      target: InstanceId | null;
    }
  /**
   * The bag is resolving (CR §7.7.4). `holder` is the player currently choosing among their
   * own waiting triggers; it moves in turn order as they run out.
   */
  | { readonly kind: 'bag'; holder: PlayerIndex }
  /**
   * One ability resolving in full (CR §6.7.2) — a triggered ability taken out of the bag, or
   * an activated ability, which never enters the bag at all (CR §7.7). Stepping it pushes the
   * `effect` frame that walks its IR; the second visit finishes the ability and settles.
   */
  | { readonly kind: 'resolve-ability'; readonly item: AbilityInstance; started: boolean }
  /**
   * One effect tree, mid-walk (SPEC.md §4.3: no game logic in suspended JS call frames).
   *
   * `queue` is the remaining IR to run, in written order; `bindings` are the cards chosen so
   * far; `awaiting` is the decision the frame is blocked on. Everything is plain data and
   * identity-free — instance ids and IR nodes only — so the frame rides into `playerView`
   * unchanged and a mid-effect state clones and serializes.
   */
  | {
      readonly kind: 'effect';
      readonly source: InstanceId;
      readonly controller: PlayerIndex;
      readonly subject: InstanceId | null;
      /** The card that caused the triggering event — `{ kind: 'event-source' }`'s referent. */
      readonly eventSource: InstanceId | null;
      /** The triggering event's numeric value — `{ kind: 'triggering-amount' }`'s referent. */
      readonly eventAmount: number | null;
      /** The defender of the triggering challenge — `{ kind: 'event-defender' }`'s referent. */
      readonly eventDefender: InstanceId | null;
      readonly label: string;
      bindings: TargetBinding[];
      /**
       * Actual numeric results earlier steps in this same effect tree bound via `as` — e.g.
       * `remove-damage`'s real amount removed, after replacements and available damage, not
       * the requested amount. `{ kind: 'bound-amount', id }` reads one back
       * (`{ kind: 'triggering-amount' }`'s sequential, effect-result counterpart).
       */
      resultBindings: ResultBinding[];
      /** Players bound by `choose-player` nodes so far (ticket 102) — `bindings`'s player twin. */
      playerBindings: PlayerBinding[];
      queue: Effect[];
      awaiting: AwaitedChoice | null;
      /**
       * The in-flight `custom()` ability, if any (SPEC.md §4.3, §5.2): its generator id plus
       * the transcript of everything already done. Replay-to-resume is exactly re-running
       * that generator over this record, so no suspended JS generator is ever stored.
       */
      custom: CustomRun | null;
    }
  /**
   * A Bodyguard character choosing whether to enter play exerted (CR §8.2.1) — a decision
   * that belongs to entering play, before "when you play" triggers resolve.
   */
  | { readonly kind: 'enter-exerted'; readonly instance: InstanceId }
  /**
   * The End-of-Turn phase (CR §3.4). `triggers` adds and resolves the phase's triggers;
   * `finish` runs the turn's final game state check and passes the turn.
   */
  | { readonly kind: 'turn-end'; step: 'triggers' | 'finish' };

/** One resolved `TargetSpec`: the cards a player picked for it. */
export interface TargetBinding {
  readonly id: string;
  instances: InstanceId[];
}

/**
 * One effect step's actual numeric result, bound via `as` for a later step in the same
 * resolution to read back (`{ kind: 'bound-amount', id }`) — resolution-local, unlike the
 * `this-turn` counters, and gone once the effect frame pops.
 */
export interface ResultBinding {
  readonly id: string;
  readonly amount: number;
}

/** One resolved `PlayerTargetSpec` (ticket 102): the player picked for it. */
export interface PlayerBinding {
  readonly id: string;
  readonly player: PlayerIndex;
}

/** What an `effect` frame is blocked on — one per pending `ChoiceRequest` shape. */
export type AwaitedChoice =
  /**
   * Picking cards for a `target` node; `picked` accumulates for `count > 1`. `spec.count` may
   * be a dynamic `Amount` (`evalAmount`, re-read on every step) — safe to re-evaluate rather
   * than cache, because nothing this session picks actually **moves** until every pick is in
   * (`finishTarget`'s `then` runs after `picked.length` reaches it), so the state the count is
   * read against does not change mid-session.
   */
  | {
      readonly kind: 'target';
      readonly spec: TargetSpec;
      picked: InstanceId[];
      readonly then: Effect;
    }
  /** Picking a player for a `choose-player` node (ticket 102) — always exactly one. */
  | {
      readonly kind: 'choose-player';
      readonly spec: PlayerTargetSpec;
      readonly then: Effect;
    }
  /**
   * "Remove up to N damage total from any number of your characters" (ticket 135) — a
   * `distribute-remove-damage` node's shared-budget decision. Alternates two rounds: pick a
   * not-yet-picked candidate (`pendingTarget === null`), then pick how much of `remaining` to
   * remove from it (`pendingTarget` set) — removed immediately, so `remaining`/`spent` are
   * running totals rather than re-evaluated on every step, unlike `target`'s `wanted`: the
   * pool genuinely shrinks as picks are made, it does not merely accumulate until one `then`.
   */
  | {
      readonly kind: 'distribute';
      readonly spec: DistributeSpec;
      readonly as?: string;
      /** Pool left to allocate. */
      remaining: number;
      /** Total actually removed so far — what `as` binds once the decision finishes. */
      spent: number;
      /** Candidates already allocated to, excluded from the next "which character" round. */
      picked: InstanceId[];
      /** Set once a candidate is chosen, awaiting its amount; `null` between rounds. */
      pendingTarget: InstanceId | null;
    }
  /**
   * A "you may" (CR §6.1.4). `chooser` is the `may` node's own reference, kept unresolved so
   * the request is rebuilt from data on every step; `otherwise` is the printed "unless".
   */
  | {
      readonly kind: 'may';
      readonly then: Effect;
      readonly label?: string;
      readonly chooser?: PlayerRef;
      readonly otherwise?: Effect;
    }
  /** A "choose one —" (CR §6.1.3). */
  | {
      readonly kind: 'modal';
      readonly modes: readonly { readonly label: string; readonly effect: Effect }[];
    }
  /** A decision a `custom()` generator yielded (SPEC.md §5.2); `custom` holds the transcript. */
  | { readonly kind: 'custom-choice'; readonly spec: CustomChoiceSpec }
  /**
   * "Each opponent chooses and discards a card" (CR §7.3). The **discarding player** decides,
   * not the ability's controller, and their hand is hidden — so the options are keyed by
   * printed id like every other choice over a hand (SPEC.md §4.4).
   */
  | { readonly kind: 'discard'; readonly player: PlayerIndex; remaining: number };

/**
 * The continuous-effect record (SPEC.md §4.5, CONTEXT.md "Modifier record") lives with the
 * model that reads it, in `modifiers.ts`: source, controller, scope, delta, duration, guard.
 * It is re-exported here because `GameState.modifiers` is the field that holds them.
 */
export type { Modifier, ModifierScope } from './modifiers.ts';

/**
 * An in-play pile of cards (SPEC.md §4.1's card stacks) lives with the model that maintains
 * it, in `stacks.ts`. It is re-exported here because `GameState.stacks` is the field that
 * holds them.
 */
export type { CardStack } from './stacks.ts';

export type FrameKind = Frame['kind'];

/** Why the game ended. */
export type WinReason =
  /** Reached the lore target at a game state check (CR §1.8.1.1). */
  | 'lore'
  /** Every other player has lost — in a 2-player game, the opponent decked out. */
  | 'deck-out';

export type GameResult =
  | { readonly kind: 'win'; readonly winner: PlayerIndex; readonly reason: WinReason }
  /** Reserved for simultaneous results (CR §1.8.4); unreachable in a vanilla 2-player game. */
  | { readonly kind: 'draw'; readonly reason: 'simultaneous' };

/** The knobs a game is created with. Plain data, so it round-trips through the action log. */
export interface RuleOptions {
  /** Lore needed to win — 20 in constructed, 15 in Pack Rush (CR §10.2). */
  readonly loreToWin: number;
  readonly startingHandSize: number;
}

export const DEFAULT_RULE_OPTIONS: RuleOptions = {
  loreToWin: 20,
  startingHandSize: 7,
};

export interface GameState {
  /**
   * Printed card data. Shared by reference with every clone — it is immutable and huge,
   * and it is the one non-plain-data field on the state.
   */
  readonly catalog: CardCatalog;
  rules: RuleOptions;
  /** Dense: `instances[id].id === id`. Never reordered, never removed. */
  instances: CardInstance[];
  players: PlayerState[];
  readonly startingPlayer: PlayerIndex;
  activePlayer: PlayerIndex;
  /** 1-based; increments when the turn passes, so each player's turn has its own number. */
  turn: number;
  phase: TurnPhase;
  rng: RngState;
  /** The resolution stack; `stack[stack.length - 1]` is the top. */
  stack: Frame[];
  /** The bag (CR §7.7) plus the triggered abilities that feed it. See `bag.ts`. */
  bag: BagState;
  /**
   * Live continuous-effect records (SPEC.md §4.5). Characteristic reads recompute from these
   * every time; `this-turn` records are dropped as the turn ends. Ticket 10 generalises them.
   */
  modifiers: Modifier[];
  /**
   * The in-play card stacks (CR §8.10, §8.3): one record per pile, holding the card in play
   * and the ordered cards under it. Piles are rare, so this is empty in almost every game
   * state and costs a clone nothing. `stacks.ts` is the only module that writes it.
   */
  stacks: CardStack[];
  /**
   * Live **payment** modifiers (CR §4.3.2.2, SPEC.md §4.1's cost system) — floating cost
   * reductions and increases, and "you may play this for free" permissions. A separate list
   * from `modifiers` because a payment modifier is not attached to a card in play: it applies
   * to a *future payment*, and is read only by `costs.ts`. `this-turn` records are dropped as
   * the turn ends (`expireCostModifiers`).
   */
  costModifiers: CostModifier[];
  /**
   * Once-per-turn ability uses, as `i<instance>:a<abilityIndex>` keys — the CR §6.1.13.2
   * "tracked per ability instance" rule, so two copies of the same card track separately.
   * Cleared in the Ready step.
   */
  abilityUses: string[];
  /**
   * Characters that have quested this turn (CR §4.5) — what "if no other character has quested
   * this turn" reads. Cleared in the Ready step, like `abilityUses`.
   */
  questedThisTurn: InstanceId[];
  /**
   * The challenger of every challenge declared this turn, oldest first — what "if it's the
   * second challenge this turn" counts. One entry **per challenge**, so a character that
   * challenges twice appears twice; cleared in the Ready step like the rest.
   */
  challengedThisTurn: InstanceId[];
  /**
   * The **defender** of every challenge declared this turn — what "chosen character who was
   * challenged this turn" reads (the opposite side of `challengedThisTurn`, which tracks the
   * challenger). Cleared in the Ready step like the rest.
   */
  wasChallengedThisTurn: InstanceId[];
  /**
   * Cards played during the current turn, oldest first — what "if you've played a song this
   * turn" reads. Instance ids only, so it carries no identity of its own; cleared in the Ready
   * step alongside the other per-turn bookkeeping.
   */
  playedThisTurn: InstanceId[];
  /**
   * Cards banished **in a challenge** during the current turn (CR §4.6.6), oldest first —
   * what "if an opposing character was banished in a challenge this turn" reads. Written
   * exactly where the `banished-in-challenge` event is emitted, so the fact and the event
   * cannot diverge; cleared in the Ready step alongside the other per-turn bookkeeping.
   */
  banishedInChallengeThisTurn: InstanceId[];
  /**
   * The card (a pile's top character or location) that had a card put under it during the
   * current turn, one entry per `card-put-under` event — what "if you've put a card under one
   * of your characters this turn" reads. Distinct from `hasCardsUnder`/`underCount`
   * (`stacks.ts`), which read current pile presence rather than placement timing. Written at
   * both Boost's and Shift's `card-put-under` emit sites (`stacks.ts`); cleared in the Ready
   * step alongside the other per-turn bookkeeping.
   */
  cardsPutUnderThisTurn: InstanceId[];
  /**
   * Cards that left **any player's** discard pile during the current turn, oldest first —
   * what "if 1 or more cards have left a player's discard this turn" reads (The Horned King -
   * Triumphant Ghoul). Every leave-reason counts (returned to hand, played from discard, put
   * under a card, shuffled into a deck, …): written once, generically, in `moveInstance`
   * wherever the *origin* zone is a discard, so the fact can never diverge from the move that
   * caused it. Cleared in the Ready step alongside the other per-turn bookkeeping.
   */
  leftDiscardThisTurn: InstanceId[];
  /**
   * Cards banished this turn **by any cause** (CR §7.6) — challenge, ability, lethal
   * non-challenge damage, cost payment — oldest first. What "if a character was banished this
   * turn" reads (Mother Gothel - Underhanded Schemer's SOMEBODY'S GOT TO USE IT), a superset of
   * `banishedInChallengeThisTurn`. Written at the same single chokepoint every banish passes
   * through (`zones.ts`'s `banish`), so the fact can never diverge from the event; cleared in
   * the Ready step alongside the other per-turn bookkeeping.
   */
  banishedThisTurn: InstanceId[];
  /**
   * Characters (and locations) that took damage this turn, one entry per `damage-dealt` event
   * — what "if one of your characters took damage this turn" reads (Devil's Eye Diamond's THE
   * PRICE OF POWER, John Silver - Vengeful Pirate's DRAWN TO A FIGHT). Any controller matching
   * a condition's own selector counts; this list itself is controller-agnostic, like
   * `banishedThisTurn`. Both `deal` and `put` verbs count — "took damage **by any means**"
   * (`TriggerCondition.damageVerb`'s own umbrella reading) — but `move` does not: moving damage
   * onto a card is neither dealing nor putting it (CR §1.9, `damage.ts`). Written at
   * `performDamage`'s single chokepoint (`damage.ts`), so the fact can never diverge from the
   * event; cleared in the Ready step alongside the other per-turn bookkeeping.
   */
  damagedThisTurn: InstanceId[];
  /**
   * Damage removed from characters controlled by each player during the current turn, indexed
   * by `PlayerIndex`. Both the `remove` and `move` verbs count, and only damage actually removed
   * is accumulated (an undamaged target contributes zero). Written at `performDamage`'s shared
   * chokepoint and cleared in the Ready step alongside the other per-turn bookkeeping.
   */
  damageRemovedThisTurn: number[];
  /** The one decision the game is waiting on. Never absent. */
  pending: ChoiceRequest;
  /** Events emitted by the most recent `apply`. Derived, never truth. */
  events: GameEvent[];
  /** `null` until a game state check ends the game. */
  result: GameResult | null;
}

/** Bitmask of every player in the game, for `CardInstance.knownTo`. */
export function allPlayersMask(playerCount: number): number {
  return (1 << playerCount) - 1;
}

/** `knownTo` bit for one player. */
export function playerBit(player: PlayerIndex): number {
  return 1 << player;
}

export function playerAt(state: GameState, index: PlayerIndex): PlayerState {
  const player = state.players[index];
  if (player === undefined) throw new Error(`No player at index ${index}`);
  return player;
}

export function instanceAt(state: GameState, id: InstanceId): CardInstance {
  const instance = state.instances[id];
  if (instance === undefined) throw new Error(`No card instance with id ${id}`);
  return instance;
}

/** The player whose turn follows this one (CR §2.1.4: turn order, wrapping). */
export function nextPlayer(state: GameState, index: PlayerIndex): PlayerIndex {
  return (index + 1) % state.players.length;
}

/** Every player once, starting from `from` — the CR's "in turn order" iteration. */
export function turnOrderFrom(state: GameState, from: PlayerIndex): PlayerIndex[] {
  const order: PlayerIndex[] = [];
  for (let i = 0; i < state.players.length; i += 1) {
    order.push((from + i) % state.players.length);
  }
  return order;
}

/** The player was the CR §2.1.3 starting/first player (ticket 107). */
export function isStartingPlayer(state: GameState, player: PlayerIndex): boolean {
  return player === state.startingPlayer;
}

/**
 * It is this player's first turn (CR §2.1.4, §8.13) — the turn-relative half of Underdog's
 * gate ("if this is your first turn"), generalised for any controller-relative first-turn
 * check (ticket 107). `turn` is 1-based and increments every time the turn passes, so each
 * player's *own* first turn lands at a fixed offset from `startingPlayer` in round-robin
 * order: turn 1 is the starting player's, turn 2 the next player's, and so on until the round
 * wraps back to the starting player at `turn = players.length + 1`.
 */
export function isYourFirstTurn(state: GameState, player: PlayerIndex): boolean {
  const offset = (player - state.startingPlayer + state.players.length) % state.players.length;
  return state.turn === offset + 1;
}

/** The ordered instance ids of one zone. Mutable — this is the live array. */
export function zoneOf(player: PlayerState, zone: Zone): InstanceId[] {
  switch (zone) {
    case 'deck':
      return player.deck;
    case 'hand':
      return player.hand;
    case 'play':
      return player.play;
    case 'inkwell':
      return player.inkwell;
    case 'discard':
      return player.discard;
    case 'under':
      return player.under;
  }
}
